import { useEffect, useState, useCallback } from "react";
import axios from "../customAxios.js";

/**
 * @typedef {object} SellerData
 * @property {number | null} id
 * @property {number | null} rut
 * @property {string | null} permission
 * @property {integer | null} admin
 * @property {object | null} company
 * @property {object | null} fares
 * @property {object | null} user
 * @property {Array<object>} warehouses
 * @property {Array<object>} operators
 */

/**
 * @typedef {object} SellerPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} SellerFilter
 * @property {string} search
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: SellerFilter, pagination: SellerPagination}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("sellersFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      const finalState = {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
      };
      return finalState;
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "sellersFilter": ${error}`,
      );
      return defaults;
    }
  }
  return defaults;
};

/**
 * Custom Hook to manage Sellers page and related actions
 *
 * It is responsible for obtaining user data when mounting, and provides
 * functions to update seller password and many future....
 * Automatically controls loading and error states
 *
 * @returns {{
 * sellers: Array<SellerData>,
 * loading: boolean,
 * error: string | null,
 * setError: React.Dispatch<React.SetStateAction<string | null>>,
 * }} An object with user status and functions to interact with the API
 *
 * @example
 * const { sellers, loading, error } = useSellers();
 */

export function useSellers() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);
  const [sellers, setSellers] = useState([]);
  const [sellersCount, setSellersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const downloadSellers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post("admins/download_admin_sellers/");
      console.log(data);
    } catch (error) {
      console.error(`No se pudo iniciar la descarga de sellers: ${error}`);
      setError("No se pudo iniciar la descarga de sellers");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      search: filters.search || undefined,
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };

    try {
      const {
        data: { results: sellers, count },
      } = await axios.get("admins/sellers/", { params });
      setSellers(sellers);
      setSellersCount(count);
    } catch (error) {
      console.error(`No se pudo obtener los sellers: ${error}`);
      setError("No se pudo obtener los sellers");
    } finally {
      setLoading(false);
    }
  }, [pagination, filters]);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "sellersFilter",
      JSON.stringify({
        filters,
        pagination,
      }),
    );
  }, [filters, pagination]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    sellers,
    sellersCount,
    loading,
    error,
    filters,
    pagination,
    auxSearcher,

    // MANNERS (Setters y Actions)
    refetchSellers: fetchSellers,
    handleFilterChange,
    handleChangePage,
    handleChangeRowsPerPage,
    setAuxSearcher,
    downloadSellers,
  };
}
